import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import { dateOnlyFromApi, formatDayMonth, lastCoveredDayFromApi } from '@plys/src/application/date-only';
import { CartState } from '@plys/src/application/states/cart.state';
import { GiftCardState } from '@plys/src/application/states/gift-card.state';
import { SubscriptionState } from '@plys/src/application/states/subscription.state';
import { CartDiscountCode, CartSummaryLineDto, SubscriptionLineRole } from '@plys/src/infrastructure/dto/products.dto';
import { isRecurringSubscription, subscriptionPeriodLabelKey } from '@plys/src/models/subscription';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { LocaleCurrencyPipe } from '../locale-currency.pipe';

/** The two days a billing window is printed as — the end already made inclusive. */
export interface BillingWindow {
  /** First day charged for, as `26.08`. */
  from: string;
  /** Last day charged for, as `31.12`. */
  to: string;
}

/**
 * One row of the first-invoice breakdown: a single charge, over the window it covers. Named either by
 * the operator's own wording (`label`, as the API reported it) or by one of ours (`labelKey`).
 */
export interface FirstInvoiceRow {
  labelKey: string | null;
  label: string | null;
  /** The subscription this charge belongs to, where the label alone would not say which. */
  productName: string | null;
  amount: number;
  window: BillingWindow | null;
  /** Discount and fee hang under the period charge they belong to. */
  child: boolean;
  /** The discount this row accounts for, so the same reduction is never listed twice. */
  discountCodeId: string | null;
}

const DISCOUNT_LABEL_KEY = 'subscriptions.summary.discount';
const SETUP_FEE_LABEL_KEY = 'subscriptions.summary.setup_fee';

/** A line's role, defaulting to the subscription itself for a payload that carries none. */
function lineRole(line: CartSummaryLineDto): SubscriptionLineRole {
  return line.subscription_line_role ?? SubscriptionLineRole.Subscription;
}

/** A row's own charge: the subscription line, never one of the children hanging off it. */
function isPeriodLine(line: CartSummaryLineDto): boolean {
  return !line.parent_cart_line_id && lineRole(line) === SubscriptionLineRole.Subscription;
}

/**
 * A reduction on the line above it: the subscription's own discount, a code applied to the cart, or
 * anything else that takes money off. Classified by what it does to the amount and not by its role
 * alone — the order's line model does not project every field the cart's does, and a child dropped
 * for lack of a role is money missing from the invoice.
 */
function isDiscountLine(line: CartSummaryLineDto): boolean {
  return lineRole(line) === SubscriptionLineRole.Discount || !!line.discount_code_id || (line.amount ?? 0) < 0;
}

/** What the cart calls the discount a line carries, where it names it at all. */
function discountName(line: CartSummaryLineDto, discounts: CartDiscountCode[]): string | null {
  const applied = discounts.find((discount) => discount.discount_code_id === line.discount_code_id);
  return applied?.name || applied?.code || null;
}

/**
 * The window a line charges for, or null while the API names none — a setup fee, or a line whose
 * start date the buyer has not settled yet. `period_end` is exclusive, so the day printed is the one
 * before it: a period ending 01.10 is paid through 30.09, and printing the raw end names a day nobody
 * is paying for.
 */
function billingWindow(line: CartSummaryLineDto): BillingWindow | null {
  const from = line.period_start ? dateOnlyFromApi(line.period_start) : null;
  const to = lastCoveredDayFromApi(line.period_end);
  return from && to ? { from: formatDayMonth(from), to: formatDayMonth(to) } : null;
}

/**
 * The children hanging off a set of lines, matched by the parent they name. Never inherited across
 * lines: a fee attributed to the wrong subscription is money the buyer cannot account for.
 */
function childrenOf(parents: CartSummaryLineDto[], all: CartSummaryLineDto[]): CartSummaryLineDto[] {
  const parentIds = new Set(parents.map((line) => line.id));
  return all.filter((line) => line.parent_cart_line_id && parentIds.has(line.parent_cart_line_id));
}

/** One purchased subscription, as the two sources report it — either side may be missing. */
interface SubscriptionInstance {
  cartLine: CartSummaryLineDto | null;
  orderLine: CartSummaryLineDto | null;
}

/**
 * The subscriptions one group of the selection holds — one per purchased card, so the same plan
 * bought twice is two rows rather than one row counted twice (PAUL-3171).
 *
 * The two sides are paired by position: the order re-issues its lines under ids of its own, so
 * nothing else ties a cart line to the order line built from it. Counted by the cart wherever the
 * cart answers for the group at all, the order being only its fallback — pairing off the longer side
 * would invent a row neither source has a charge for. Neither side is a group previewed before its
 * first line is reserved, which is one row at the plan's own price.
 */
function instancesOf(cartLines: CartSummaryLineDto[], orderLines: CartSummaryLineDto[]): SubscriptionInstance[] {
  const count = cartLines.length || orderLines.length || 1;
  return Array.from({ length: count }, (_, index) => ({
    cartLine: cartLines[index] ?? null,
    orderLine: orderLines[index] ?? null,
  }));
}

/**
 * The first invoice for one summary row, line by line: what each subscription's first period costs,
 * with the reduction and the joining fee that line carries underneath it.
 *
 * Per line, as the row above it now is (PAUL-3171): two cards of one plan on one period are charged
 * apart, and a discount may apply to only one of them. Every window is read off the line that
 * carries it and never inherited: a discount whose duration is shorter than a pro-rated first period
 * stops partway through it, and the parent's window would print weeks of discount nobody was given
 * (PAUL-3292).
 */
function firstInvoiceRows(
  parents: CartSummaryLineDto[],
  children: CartSummaryLineDto[],
  productName: string,
  periodLabelKey: string | null,
  discounts: CartDiscountCode[],
  nameFeeProduct: boolean
): FirstInvoiceRow[] {
  return parents.flatMap((parent) => {
    const own = children.filter((child) => child.parent_cart_line_id === parent.id);
    const rows: FirstInvoiceRow[] = [
      {
        // The period charged for, as the row above names it ("Maandelijks"); a passepartout or
        // punchcard has no period, so it is named by the product instead.
        labelKey: periodLabelKey,
        label: periodLabelKey ? null : productName,
        productName: null,
        amount: parent.amount ?? 0,
        window: billingWindow(parent),
        child: false,
        discountCodeId: null,
      },
    ];
    own
      .filter((child) => isDiscountLine(child))
      .forEach((child) => {
        const name = discountName(child, discounts);
        rows.push({
          labelKey: name ? null : DISCOUNT_LABEL_KEY,
          label: name,
          productName: null,
          amount: child.amount ?? 0,
          window: billingWindow(child),
          child: true,
          discountCodeId: child.discount_code_id ?? null,
        });
      });
    // Everything else the line carries is a charge on top of it, which in a subscription cart is the
    // joining fee. Taken as the complement of the reductions so no child can fall between the two.
    const fees = own.filter((child) => !isDiscountLine(child));
    fees.forEach((child) =>
      rows.push({
        // The operator's own wording for the fee ("Eenmalige aansluitingskosten") wins over our
        // generic key — it is what the invoice will say.
        labelKey: child.title ? null : SETUP_FEE_LABEL_KEY,
        label: child.title || null,
        // Named after the fee only where the invoice holds more than one subscription: repeating the
        // one product's name on every fee row buries the amounts under it.
        productName: nameFeeProduct ? productName : null,
        amount: child.amount ?? 0,
        window: null,
        child: true,
        discountCodeId: null,
      })
    );
    // An order placed before the fee got a line of its own still reports it on the subscriber block;
    // read it only when no fee line is there, or the fee would be charged twice.
    const legacyFee = fees.length ? 0 : parent.subscriber?.setup_fee_amount ?? 0;
    if (legacyFee) {
      rows.push({
        labelKey: SETUP_FEE_LABEL_KEY,
        label: null,
        productName: nameFeeProduct ? productName : null,
        amount: legacyFee,
        window: null,
        child: true,
        discountCodeId: null,
      });
    }
    // A charge of nothing is not a charge: a fee configured at zero would print a €0,00 row.
    return rows.filter((row) => row.amount !== 0);
  });
}

/**
 * Simplified counterpart of <plys-cart-preview> for the subscription checkout.
 * No images, no tickets/discounts — just the subscriptions in the cart, the gift cards a prepaid shop
 * allows next to them, and a total. Reads straight from the states so every step renders the same
 * summary without inputs. Emits null while the cart is empty — the summary stays blank rather than
 * showing a made-up price.
 *
 * One row per purchased subscription — two cards of one plan are two lines, never a single line
 * counted "× 2" (PAUL-3171). The rows are always the plan on offer at its per-period price;
 * {@link firstTermCharge} adds the first invoice underneath them.
 */
@Component({
  selector: 'plys-subscription-cart-preview',
  standalone: true,
  imports: [LocaleCurrencyPipe, CommonModule, TranslocoModule],
  templateUrl: './subscription-cart-preview.component.html',
  styleUrls: ['./subscription-cart-preview.component.scss'],
})
export class SubscriptionCartPreviewComponent {
  /** The mobile footer renders its own total row, so it suppresses this one. */
  @Input() hideSummary = false;

  /**
   * Break the first invoice out under the plan: what the payment provider is about to debit, line by
   * line, and as a total.
   *
   * Set on the payment step, and only there. The earlier steps are about the plan on offer — the
   * buyer is comparing periods, not authorising a charge — but on the payment step "€ 50,00 per
   * maand" alone is what let this panel contradict the payment provider: a periodic plan's first term
   * is pro-rated from its start date, carries a joining fee and may have a discount applied, none of
   * which the period price says anything about. The rows keep saying what the plan costs per period;
   * the block underneath says what is being paid now, and which days it buys.
   *
   * The first invoice and nothing else: what the following periods cost is the web portal's to show
   * (PO call), so no "and € X per month from then on" line here. Every figure comes from the cart or
   * the order built from it; this component computes no pro-rata of its own.
   */
  @Input() set firstTermCharge(value: boolean) {
    this._firstTermCharge.next(!!value);
  }

  private readonly _firstTermCharge = new BehaviorSubject(false);

  readonly vm$ = combineLatest([
    this.subscriptionState.selections$,
    this.subscriptionState.selectedProduct$,
    this.subscriptionState.selectedBillingPeriod$,
    this.giftCardState.selections$,
    this.cartState.total$,
    this.cartState.discounts$,
    this.cartState.lines$,
    this.cartState.orderLines$,
    this._firstTermCharge,
  ]).pipe(
    map(
      ([
        selections,
        selectedProduct,
        selectedPeriod,
        giftCards,
        cartTotal,
        discounts,
        lines,
        orderLines,
        firstTerm,
      ]) => {
        // Before any cart line is reserved (period-selection step) the cart holds nothing, so the
        // highlighted plan is previewed at one rather than showing a €0 total.
        const rows = selections.length
          ? selections
          : selectedProduct
          ? [{ product: selectedProduct, billingPeriod: selectedPeriod, quantity: 0, cartLineIds: [] }]
          : [];
        if (!rows.length && !giftCards.length) {
          return null;
        }
        const quantity = selections.reduce((sum, selection) => sum + selection.quantity, 0);
        // A recurring order only ever holds the one plan, though possibly on more than one billing
        // period — each of those is a row of its own, billed differently. A prepaid one may hold
        // several products, all of them paid once.
        const isRecurring = rows.some(({ product }) => isRecurringSubscription(product.type));
        // The first invoice is what a recurring purchase needs spelling out: a passepartout, punchcard
        // or gift card is charged its own price once, which the rows already say. A gift card next to a
        // plan would be charged but not listed, so such a basket keeps the plain summary.
        const showInvoice = firstTerm && isRecurring && !giftCards.length;
        // Whether a charge has to say which subscription it belongs to. One plan on two billing
        // periods is still the one plan, and its name on every fee row only buries the amounts.
        const nameFeeProduct = new Set(rows.map(({ product }) => product.id)).size > 1;
        const linesById = new Map(lines.map((line) => [line.id, line]));
        const subscriptions = rows.flatMap(({ product, billingPeriod, cartLineIds }) => {
          // The order's own lines, matched to this group by product and billing period — which is
          // exactly what makes a group (see SubscriptionSelection) — rather than by id: the order
          // re-issues its lines and nothing promises they keep the cart's ids.
          const groupOrderLines = orderLines.filter(
            (line) =>
              isPeriodLine(line) &&
              line.product_id === product.id &&
              (line.subscriber?.billing_period_id ?? null) === (billingPeriod?.id ?? null)
          );
          const groupCartLines = cartLineIds
            .map((id) => linesById.get(id))
            .filter((line): line is CartSummaryLineDto => !!line);
          // What one subscription of this group costs per period — never the current selection's price,
          // which says nothing about a line reserved on another period. A passepartout or punchcard is
          // sold at its own price. Pro-rating, fees and discounts stay out of it: that is the invoice's
          // job, and mixing the two is what made this panel disagree with the amount being debited.
          const periodPrice = isRecurringSubscription(product.type)
            ? billingPeriod?.price ?? product.price
            : product.price;
          const periodLabelKey =
            isRecurringSubscription(product.type) && billingPeriod
              ? subscriptionPeriodLabelKey(billingPeriod.periodType)
              : null;
          // One row per purchased subscription rather than one per group with a "× 2" behind it
          // (PAUL-3171): each card is dated, discounted and invoiced on its own, so a counted row
          // could only ever name the price the two happen to share.
          return instancesOf(groupCartLines, groupOrderLines).map(({ cartLine, orderLine }) => {
            // The cart is read in preference to the order, though the order is what gets charged: the
            // total this block adds up to is the cart's, the order is built from these very lines, and
            // the order's line model projects less of them — no operator fee label, and not every
            // reduction. Reading the order left the block re-rendering shorter halfway through the step
            // and € 21,55 of discount unaccounted for. The order is the fallback, for a row the cart
            // cannot answer for.
            const fromCart = !!cartLine;
            const rowLine = fromCart ? cartLine : orderLine;
            const rowLines = rowLine ? [rowLine] : [];
            // The fee and the discount hang off a line as children, and belong to the summary that line
            // came from — a cart line's children are in the cart.
            const children = childrenOf(rowLines, fromCart ? lines : orderLines);
            // A one-off joining fee, charged on the first invoice only. Broken out here for the steps that
            // show no invoice — a price the buyer will be charged should not first appear on the payment
            // step (PAUL-3170). Summed over the line's children: one line may report several.
            const feeLines = children.filter((line) => !isDiscountLine(line));
            const setupFee = feeLines.length
              ? feeLines.reduce((sum, line) => sum + (line.amount ?? 0), 0)
              : rowLines.reduce((sum, line) => sum + (line.subscriber?.setup_fee_amount ?? 0), 0);
            return {
              name: product.name,
              // What one of these costs per period — a rate, which is why it keeps its "maandelijks".
              price: periodPrice,
              periodLabelKey,
              setupFee: !showInvoice && setupFee > 0 ? setupFee : null,
              setupFeeLabel: feeLines.find((line) => line.title)?.title ?? null,
              invoice: showInvoice
                ? firstInvoiceRows(rowLines, children, product.name, periodLabelKey, discounts, nameFeeProduct)
                : [],
            };
          });
        });
        // Passepartouts, punchcards and gift cards are paid once, so their total is the cart total —
        // which already covers a mixed prepaid basket.
        const hasReservedLines = quantity > 0 || giftCards.length > 0;
        const totalOnce = hasReservedLines ? cartTotal : subscriptions[0]?.price ?? null;
        // One row per purchased gift card, the same way the subscriptions above are listed
        // (PAUL-3171). The amount is per row because a gift card sold by predefined amounts has no
        // price of its own, and the same product bought at two amounts is priced apart (PAUL-3071).
        const giftCardRows = giftCards.flatMap(({ product, amount, quantity: rowQuantity }) =>
          Array.from({ length: rowQuantity }, () => ({
            name: product.name,
            unitPrice: amount ?? product.price,
            currency: product.currency,
          }))
        );
        const invoice = subscriptions.flatMap((row) => row.invoice);
        // A discount the cart applied that none of the lines accounted for — one covering the order
        // rather than a line of it. Listed at the end so nothing invisible reduces what is charged. A
        // non-negative amount is the cart's way of saying the code is not actually reducing anything.
        const listed = new Set(invoice.map((row) => row.discountCodeId).filter((id) => !!id));
        const unlisted: FirstInvoiceRow[] = invoice.length
          ? discounts
              .filter((discount) => discount.amount < 0 && !listed.has(discount.discount_code_id))
              .map((discount) => ({
                labelKey: discount.name || discount.code ? null : DISCOUNT_LABEL_KEY,
                label: discount.name || discount.code || null,
                productName: null,
                amount: discount.amount,
                window: null,
                child: true,
                discountCodeId: discount.discount_code_id,
              }))
          : [];
        return {
          subscriptions,
          giftCards: giftCardRows,
          // The charges the payment provider is about to be handed, line by line; null wherever the
          // panel is only showing what the plan costs.
          firstInvoice: invoice.length ? [...invoice, ...unlisted] : null,
          // While the buyer is choosing, a recurring plan shows no total: the per-period price is
          // already on the line above it, and a total next to it only invites the question of which
          // period it covers. One-time products (passepartout, punchcard, gift card) keep theirs. On
          // the payment step every plan has one — the sum that is about to be debited.
          total: firstTerm ? (hasReservedLines ? cartTotal : null) : isRecurring ? null : totalOnce,
          // What the total is the total of: the invoice above it, wherever one is actually listed.
          totalLabelKey: invoice.length ? 'subscriptions.summary.total_first_invoice' : 'global.total',
        };
      }
    )
  );

  constructor(
    private readonly subscriptionState: SubscriptionState,
    private readonly giftCardState: GiftCardState,
    private readonly cartState: CartState
  ) {}
}
